import { isValidCpf } from '@brazilian-utils/is-valid-cpf';
import ErrorMessage from '@components/atoms/ErrorMessage';
import FormField from '@components/atoms/FormField';
import Spacing from '@components/atoms/Spacing';
import TextField from '@components/atoms/TextField';
import { Paragraph } from '@components/atoms/Typography';
import BotExplanation from '@components/molecules/BotExplanation';
import RadioGroup from '@components/molecules/RadioGroup';
import { MaritalStatus, Sex } from '@graphql/generated';
import {
  MainConductorFormResult,
  QuoteInitialForm,
} from '@lib/data/typesQuestions';
import { reportError } from '@lib/services/sentry';
import { dateToUnixTimestamp, unixTimestampToDate } from 'helpers';
import GridBox from 'components/atoms/GridBox';
import OnboardingButtonBar from 'components/molecules/button-bar/OnboardingButtonBar';
import React, { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import styled from 'styled-components';
import { maritalStatusOptions } from './MaritalStatusForm';

const Form = styled.form``;

const sexOptions = [
  {
    label: 'Masculino',
    value: Sex.Male,
  },
  {
    label: 'Feminino',
    value: Sex.Female,
  },
];

interface Props {
  onSubmit: (data: MainConductorFormResult) => void;
  loading: boolean;
  item: QuoteInitialForm;
}

const MainConductorForm: FC<Props> = ({ onSubmit, loading, item }) => {
  const {
    register,
    handleSubmit,
    errors,
    watch,
    control,
    getValues,
    clearErrors,
  } = useForm<{
    mainConductor: 'yep' | 'nop';
    name?: string;
    birthday?: string;
    cpf?: string;
    maritalStatus?: { value: MaritalStatus };
    sex?: { value: Sex };
  }>({
    defaultValues: {
      mainConductor: item.mainConductor?.cpf ? 'nop' : 'yep',
    },
  });

  return (
    <Form
      onSubmit={handleSubmit((data) => {
        if (data.mainConductor === 'yep') {
          onSubmit({ mainConductor: null });
        } else if (data.birthday && data.cpf && data.name && data.sex) {
          const [day, month, year] = data.birthday.split('/').map(Number);

          onSubmit({
            mainConductor: {
              birthday: dateToUnixTimestamp(new Date(year, month - 1, day)),
              cpf: data.cpf,
              name: data.name,
              maritalStatus: data.maritalStatus?.value,
              sex: data.sex?.value,
            },
          });
        }
      })}
    >
      <GridBox justifyContent="center">
        <GridBox width={[1, 0.5, 0.5]}>
          <RadioGroup
            inputRef={register({ required: 'Escolha uma das opções' })}
            errorMessage={errors.mainConductor?.message}
            onSelected={(value) => {
              clearErrors();
              if (typeof getValues().cpf === 'undefined' && value === 'yep') {
                onSubmit({ mainConductor: null });
              }
            }}
            name="mainConductor"
            options={[
              {
                label: 'Sim',
                value: 'yep',
                checked: !item.mainConductor?.cpf,
              },
              {
                label: 'Não',
                value: 'nop',
                checked: !!item.mainConductor?.cpf,
              },
            ]}
          />
        </GridBox>
      </GridBox>
      {watch().mainConductor === 'nop' && (
        <>
          <Spacing vertical={2}>
            <Paragraph center>
              <strong>Informe os dados do condutor principal</strong>
            </Paragraph>
          </Spacing>
          <GridBox justifyContent="space-between">
            <GridBox width={[1, 0.45, 0.45]}>
              <FormField style={{ width: '100%' }} label="Nome">
                <TextField
                  placeholder="Nome"
                  name="name"
                  errorMessage={errors.name?.message}
                  inputRef={register({
                    required: 'Precisamos do seu completo',
                    validate: {
                      fullName: (name: string) => {
                        const names = name.split(' ').filter((n) => n.trim());

                        const allNamesHas2Characters = names.every(
                          (str) => str.length >= 2,
                        );

                        const hasMoreThan2Names = names.length >= 2;

                        return (
                          (allNamesHas2Characters && hasMoreThan2Names) ||
                          'Precisamos do seu completo '
                        );
                      },
                    },
                  })}
                />
              </FormField>
            </GridBox>

            <GridBox width={[1, 0.45, 0.45]}>
              <FormField style={{ width: '100%' }} label="Data de Nascimento ">
                <TextField
                  placeholder="00/00/0000"
                  name="birthday"
                  defaultValue={
                    item.mainConductor?.birthday
                      ? unixTimestampToDate(
                          item.mainConductor.birthday,
                        ).toLocaleDateString('pt-BR', {
                          month: '2-digit',
                          day: '2-digit',
                          year: 'numeric',
                        })
                      : ''
                  }
                  type="tel"
                  errorMessage={errors.birthday?.message}
                  mask={[
                    /\d/,
                    /\d/,
                    '/',
                    /\d/,
                    /\d/,
                    '/',
                    /\d/,
                    /\d/,
                    /\d/,
                    /\d/,
                  ]}
                  inputRef={register({
                    required: 'Precisamos da data de nascimento',
                    validate: {
                      valid: (date: string) => {
                        try {
                          const [day, month, year] = date
                            .split('/')
                            .map(Number);

                          if (day > 31 || month > 12 || year < 1900)
                            return 'Data de nascismento inválida';

                          const d = new Date(year, month - 1, day);

                          const now = new Date();
                          now.setFullYear(now.getFullYear() - 18);

                          if (now.getTime() < d.getTime())
                            return 'Você precisa ter mais de 18 anos para cotar o seguro';

                          return true;
                        } catch (error) {
                          reportError(error);
                          return 'Data de nascismento inválida';
                        }
                      },
                    },
                  })}
                />
              </FormField>
            </GridBox>

            <GridBox width={[1, 0.45, 0.45]}>
              <FormField label="Estado Civil" style={{ width: '100%' }}>
                <Controller
                  name="maritalStatus"
                  control={control}
                  rules={{ required: 'Precisamos do estado civil' }}
                  defaultValue={maritalStatusOptions.find(
                    (m) => m.value === item.mainConductor?.maritalStatus,
                  )}
                  render={(field) => (
                    <Select
                      {...field}
                      options={maritalStatusOptions}
                      placeholder="Qual o Estado Civil"
                      noOptionsMessage={() => 'Não encontrado'}
                    />
                  )}
                />
                {(errors.maritalStatus as { message?: string })?.message && (
                  <ErrorMessage>
                    {(errors.maritalStatus as { message?: string })?.message}
                  </ErrorMessage>
                )}
              </FormField>
            </GridBox>

            <GridBox width={[1, 0.45, 0.45]}>
              <FormField label="Sexo" style={{ width: '100%' }}>
                <Controller
                  name="sex"
                  control={control}
                  defaultValue={sexOptions.find(
                    (m) => m.value === item.mainConductor?.sex,
                  )}
                  rules={{ required: 'Precisamos do Sexo' }}
                  render={(field) => (
                    <Select
                      {...field}
                      options={sexOptions}
                      placeholder="Qual o Sexo"
                      noOptionsMessage={() => 'Não encontrado'}
                    />
                  )}
                />
                {(errors.sex as { message?: string })?.message && (
                  <ErrorMessage>
                    {(errors.sex as { message?: string })?.message}
                  </ErrorMessage>
                )}
              </FormField>
            </GridBox>
          </GridBox>

          <GridBox justifyContent="center">
            <GridBox width={[1, 0.45, 0.45]}>
              <FormField label="cpf" style={{ width: '100%' }}>
                <TextField
                  name="cpf"
                  defaultValue={item.mainConductor?.cpf || ''}
                  placeholder="000.000.000-00"
                  inputRef={register({
                    required: 'Precisamos de um CPF válido',
                    validate: {
                      valid: (s) =>
                        isValidCpf(s) || 'Precisamos de um CPF válido',
                      diff: (s) =>
                        s !== item.conductor?.cpf ||
                        'O CPF deve ser diferente do seu',
                    },
                  })}
                  type="tel"
                  errorMessage={errors?.cpf?.message}
                  mask={[
                    /\d/,
                    /\d/,
                    /\d/,
                    '.',
                    /\d/,
                    /\d/,
                    /\d/,
                    '.',
                    /\d/,
                    /\d/,
                    /\d/,
                    '-',
                    /\d/,
                    /\d/,
                  ]}
                />
              </FormField>
            </GridBox>
          </GridBox>
        </>
      )}

      {watch().mainConductor !== 'nop' && (
        <BotExplanation>
          Principal condutor: É a pessoa que utiliza o veículo a maior parte do
          tempo de circulação, ainda que outras pessoas possam, em situações
          eventuais (no máximo 2 dias por semana) também utiliza-lo.
        </BotExplanation>
      )}
      <OnboardingButtonBar loading={loading} />
    </Form>
  );
};

export default MainConductorForm;
